<template>
  <section>
    <TableFilterView
      :columns="columns"
      :initial-filters="activeFilters"
      :show-export="false"
      @update-active-filters="updateActiveFilters"
      @update-visible-columns="updateVisibleColumns"
    />
    <b-table
      :ref="tableRef"
      :striped="config.isStriped"
      :hoverable="config.isHoverable"
      :debounce-search="config.searchDebounce"
      :aria-next-label="config.nextLabel()"
      :aria-previous-label="config.previousLabel()"
      :aria-page-label="config.pageLabel()"
      :aria-current-label="config.currentPageLabel()"
      :per-page="config.perPage"
      :sticky-header="config.stickyHeader"
      :current-page="currentPagedSortedParameter.pageNumber"
      :pagination-position="config.paginationPosition"
      paginated
      backend-pagination
      :sort-icon="config.sortIcon"
      :sort-icon-size="config.sortIconSize"
      backend-sorting
      sort-multiple
      :sort-multiple-data="buefySortingPriority"
      :total="total"
      :data="referentsList"
      :loading="isLoading"
      @sort="onSort"
      @sorting-priority-removed="sortingPriorityRemoved"
      @page-change="onPageChange"
    >
      <b-table-column
        v-for="column in columns"
        :key="column.field"
        v-bind="column"
        :visible="column.visible"
      >
        <template #header>
          <b-tooltip
            v-if="column.tooltip"
            size="is-small"
            append-to-body
            dashed
            position="is-bottom"
            animated
          >
            {{ column.label }}
            <template #content
              ><span>{{ column.tooltip }}</span>
            </template>
          </b-tooltip>
          <span v-else> {{ column.label }}</span>
        </template>
        <template #default="props">
          <div
            v-if="!column.custom"
            class="clickable"
            @click="showUserDetailsPage($event, props.row['id'])"
          >
            <div>{{ props.row[column.field] }}</div>
          </div>
        </template>
      </b-table-column>
    </b-table>
  </section>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { FilterDTO, PaginationOrder, UtilisateurDTO } from "@/model/bean/GeneratedDTOs"
import { UsersService } from "@/services/rest/users/UsersService"
import Column from "@/views/tables/Column"
import TableFilterView from "@/views/tables/TableFilterView.vue"
import { useTableView } from "@/hooks/useTableView"
import { SortOrder } from "@/model/SortOrder"
import { eventBus } from "@/services/EventBus"
import { BTable, BTableColumn, BTooltip } from "buefy"

export default defineComponent({
  name: "StructureReferentsView",
  components: {
    TableFilterView,
    BTooltip,
    BTable,
    BTableColumn,
  },
  props: {
    structureId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      usersService: UsersService.INSTANCE,
      referentsList: [] as Array<UtilisateurDTO>,
      tableView: null as any,
    }
  },
  computed: {
    // Propriétés réactives du tableau
    tableRef(): string {
      return this.tableView?.state.tableRef || ""
    },
    isLoading(): boolean {
      return this.tableView?.state.isLoading || false
    },
    columns(): Array<Column> {
      return this.tableView?.state.columns || []
    },
    currentPagedSortedParameter(): any {
      return this.tableView?.state.currentPagedSortedParameter
    },
    buefySortingPriority(): Array<any> {
      return this.tableView?.state.buefySortingPriority || []
    },
    total(): number {
      return this.tableView?.state.total || 0
    },
    config(): any {
      return this.tableView?.state.config
    },
    activeFilters(): Array<any> {
      return this.tableView?.state.activeFilters || []
    },
  },

  created() {
    // Initialisation du hook useTableView dans mounted
    this.tableView = useTableView({
      tableRef: "structure-referents-table",
      router: this.$router,
      route: this.$route,
      i18n: this.$i18n,
      getInitialColumns: this.getInitialColumns,
      getDefaultSort: this.getDefaultSort,
      getInitialActiveFilters: this.getInitialActiveFilters,
      reloadData: async () => {
        await this.reloadData()
      },
    })
  },
  mounted() {
    this.tableView.initMounted()
  },
  methods: {
    // Réexposer les méthodes du tableau
    updateVisibleColumns(newVisibleColumnList: Array<Column>): void {
      this.tableView?.updateVisibleColumns(newVisibleColumnList)
    },

    updateActiveFilters(filters: Array<any>): void {
      this.tableView?.updateActiveFilters(filters)
    },

    onPageChange(newPage: string | number): void {
      const pageNum = typeof newPage === "string" ? parseInt(newPage) : newPage
      this.tableView.onPageChange(pageNum)
    },

    onSort(field: string | undefined, order: SortOrder): void {
      if (field) {
        this.tableView.onSort(field, order)
      }
    },

    sortingPriorityRemoved(field: string | undefined): void {
      if (field) {
        this.tableView.sortingPriorityRemoved(field)
      }
    },

    updateTotal(total: number): void {
      this.tableView?.updateTotal(total)
    },

    getInitialActiveFilters(): Array<FilterDTO> {
      return new Array<FilterDTO>()
    },

    async reloadData(): Promise<void> {
      try {
        const paginatedReferentsList = await this.usersService.getReferents(
          this.structureId,
          this.currentPagedSortedParameter,
          this.activeFilters,
        )
        this.referentsList = paginatedReferentsList.elements
        this.updateTotal(paginatedReferentsList.count)
      } catch (e: any) {
        // Silent catch
      }
    },

    getInitialColumns(): Array<Column> {
      return [
        {
          field: "prenom",
          label: this.$t("structurePage.referents.prenom").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "nom",
          label: this.$t("structurePage.referents.nom").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "telephone",
          label: this.$t("structurePage.referents.telephone").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "courriel",
          label: this.$t("structurePage.referents.courriel").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
      ]
    },

    getDefaultSort(): Array<PaginationOrder> {
      const defaultSort = new PaginationOrder()
      defaultSort.clause = "nom"
      defaultSort.desc = true
      return [defaultSort]
    },

    showUserDetailsPage(event: Event, id: number): void {
      // Do not foward click event to row (would trigger modal)
      event.stopPropagation()
      //For APP to open ProfileModale
      eventBus.emit("open-profile-modale", { show: true, id: id.toString() })
    },
  },
})
</script>
